/**
 * Alt text for Shopify product images (T-165): one sentence per image, written by Haiku from the
 * product and social picks tagging's description of the picture (no new look at the image).
 * Design: docs/product-image-alt-text-design.md.
 */
import { z } from 'zod';
import type { JobRunSummary } from './types/jobs';

/** A hard limit: longer sentences are asked for once more, then trimmed at a word. */
export const ALT_TEXT_MAX_CHARS = 125;

/** Images per Haiku call. */
export const ALT_TEXT_IMAGES_PER_CALL = 20;

/**
 * Shopify media that gets alt text: images, and videos Shopify hosts, described from the video
 * analysis. YouTube and Vimeo links and 3D models aren't Shopify files, so they have none here.
 */
export const ALT_TEXT_MEDIA_TYPES = ['IMAGE', 'VIDEO'] as const;

export function hasAltText(mediaContentType: string | null | undefined): boolean {
  return (ALT_TEXT_MEDIA_TYPES as readonly string[]).includes(mediaContentType ?? '');
}

/** The SQL list of `ALT_TEXT_MEDIA_TYPES`, for `media_content_type IN (…)`. */
export const ALT_TEXT_MEDIA_TYPES_SQL = ALT_TEXT_MEDIA_TYPES.map((t) => `'${t}'`).join(', ');

/** Who wrote an image's current alt text in Shopify. Null: there is none yet. */
export const ALT_TEXT_SOURCES = ['ai', 'person', 'template'] as const;
export type AltTextSource = (typeof ALT_TEXT_SOURCES)[number];

/** A product_media_alt_text row's state. */
export const ALT_TEXT_ROW_STATUSES = ['suggested', 'saved', 'failed'] as const;
export type AltTextRowStatus = (typeof ALT_TEXT_ROW_STATUSES)[number];

/** How a reviewed suggestion was saved, for automatic saving's rate. */
export const ALT_TEXT_REVIEW_OUTCOMES = ['as_written', 'edited'] as const;
export type AltTextReviewOutcome = (typeof ALT_TEXT_REVIEW_OUTCOMES)[number];

/**
 * An image's alt text status, as the Media page filters and shows it.
 * - missing: no alt text, or the old template, and no suggestion yet
 * - suggested: jflo has a suggestion waiting for review
 * - saved: jflo's text is on Shopify
 * - person: someone wrote it, in jflo or in Shopify; jflo never rewrites it
 * - failed: the last save to Shopify failed
 */
export const ALT_TEXT_STATUSES = ['missing', 'suggested', 'saved', 'person', 'failed'] as const;
export type AltTextStatus = (typeof ALT_TEXT_STATUSES)[number];

export const ALT_TEXT_STATUS_LABELS: Record<AltTextStatus, string> = {
  missing: 'Missing',
  suggested: 'Suggested',
  saved: 'Saved',
  person: 'Edited by hand',
  failed: 'Failed',
};

export function isAltTextStatus(value: string): value is AltTextStatus {
  return (ALT_TEXT_STATUSES as readonly string[]).includes(value);
}

/**
 * The shop name the old template ended with ("Petromax - Cooking Stand PM_PT - Brave Hardy"). Alt
 * text ending in "- <shop name>" or holding a SKU is the template: jflo's to replace. Anything else
 * is a person's (the SQL is in worker/src/services/product-alt-text/sql.ts).
 */
export const ALT_TEXT_TEMPLATE_SHOP_NAME = 'Brave Hardy';

/** Words a trimmed sentence shouldn't end on. */
const DANGLING = new Set(['a', 'an', 'and', 'the', 'with', 'of', 'in', 'on', 'by', 'for', 'to', 'from', 'at', 'or', 'its', 'their', 'into', 'beside', 'under', 'over']);

/** One line, no wrapping quotes, no trailing full stop. */
export function tidyAltText(text: string): string {
  let out = text.replace(/\s+/g, ' ').trim();
  out = out.replace(/^["'“‘]+|["'”’]+$/g, '').trim();
  out = out.replace(/\.+$/, '').trim();
  return out;
}

/** Cut to `max` characters at a word, without ending on "and", "with", a comma or the like. */
export function trimAltTextAtWord(text: string, max = ALT_TEXT_MAX_CHARS): string {
  const tidy = tidyAltText(text);
  if (tidy.length <= max) return tidy;
  const words = tidy.slice(0, max + 1).split(' ');
  // The last piece may be a word cut in half.
  if (tidy.length > max && tidy[max] !== ' ') words.pop();
  while (words.length > 1) {
    const last = words[words.length - 1].replace(/[,;:–—-]+$/, '');
    if (!last || DANGLING.has(last.toLowerCase())) {
      words.pop();
      continue;
    }
    words[words.length - 1] = last;
    break;
  }
  return tidyAltText(words.join(' ').slice(0, max));
}

/** What the model is told about one image: the product, and the parts of tagging's description that matter. */
export type AltTextBrief = {
  ref: string;
  /** Only for a video; an image has none. */
  kind?: 'video';
  product: string;
  brand: string | null;
  product_type: string | null;
  variant: string | null;
  shows: Record<string, unknown>;
};

type DescriptionLike = {
  description?: string;
  shot_type?: string;
  scene?: Record<string, unknown>;
  people?: { count?: number; activity?: string };
  activities?: string[];
  product?: { visible?: string; in_use?: boolean; count?: number; colours?: string[] };
  text_and_graphics?: { text_amount?: string; text_content?: string; elements?: string[] };
};

type VideoAnalysisLike = {
  summary?: string;
  format?: string;
  on_screen_text?: string[];
  people?: { max_count?: number };
  settings?: string[];
  scenes?: Array<{ description?: string }>;
};

/**
 * The trial's trimmed description (2026-10-16): no ratings, photography or background. A video is
 * described by its analysis instead (shared/social-video.ts): what happens in it, not its cover.
 */
export function altTextBrief(input: {
  ref: string;
  title: string;
  brand: string | null;
  productType: string | null;
  variant: string | null;
  description: unknown;
  video?: unknown;
}): AltTextBrief {
  const product = {
    ref: input.ref,
    product: input.title,
    brand: input.brand || null,
    product_type: input.productType || null,
    variant: input.variant || null,
  };
  if (input.video && typeof input.video === 'object') {
    const v = input.video as VideoAnalysisLike;
    return {
      ...product,
      kind: 'video',
      shows: {
        summary: v.summary,
        format: v.format,
        settings: v.settings,
        people: v.people?.max_count ? { count: v.people.max_count } : null,
        scenes: (v.scenes ?? []).map((scene) => scene.description).filter(Boolean),
        on_screen_text: v.on_screen_text?.length ? v.on_screen_text : null,
      },
    };
  }
  const d = (input.description && typeof input.description === 'object' ? input.description : {}) as DescriptionLike;
  const s = d.scene ?? {};
  const p = d.people ?? {};
  const pr = d.product ?? {};
  const t = d.text_and_graphics ?? {};
  return {
    ...product,
    shows: {
      summary: d.description,
      shot_type: d.shot_type,
      setting: {
        location: s.location,
        environments: s.environments,
        time_of_day: s.time_of_day,
        fire_or_flame: s.fire_or_flame,
        food_or_drink: s.food_or_drink,
        vehicle: s.vehicle,
        animals: s.animals,
      },
      people: p.count ? { count: p.count, activity: p.activity } : null,
      activities: d.activities,
      product: { visible: pr.visible, in_use: pr.in_use, count: pr.count, colours: pr.colours },
      text: !t.text_amount || t.text_amount === 'none' ? null : { text_content: t.text_content, elements: t.elements },
    },
  };
}

// ── Automatic saving (Q10) ───────────────────────────────────────────────────

/**
 * Reviewed suggestions the rate is taken over: 10, so a person can earn it in one sitting (Andrew,
 * 2026-10-16; the design had 200). At 95% that means all 10 saved as written; it switches off at 8.
 */
export const ALT_TEXT_AUTO_SAVE_WINDOW = 10;
/** Saved as written, of the window, for automatic saving to switch itself on. */
export const ALT_TEXT_AUTO_SAVE_ON_RATE = 0.95;
/** Below this it switches itself off again. */
export const ALT_TEXT_AUTO_SAVE_OFF_RATE = 0.9;

/** One of the reviews automatic saving's rate is taken over: what jflo suggested, and what was saved. */
export type AltTextReview = {
  product_media_id: string;
  product_id: string;
  product_title: string | null;
  media_content_type: string | null;
  image_url: string | null;
  suggested: string | null;
  /** What was saved: the suggestion as written, or the person's edit. */
  saved: string | null;
  outcome: AltTextReviewOutcome;
  reviewed_at: string;
  reviewed_by: string | null;
};

export type AltTextAutoSaveRate = {
  /** Reviewed suggestions from the current prompt and model, at most the window. */
  reviewed: number;
  /** Of those, saved without an edit. */
  asWritten: number;
};

/**
 * Whether automatic saving is on after this rate. It needs a full window from the current prompt
 * and model (a prompt or model change starts the count again), switches on at 95% and off below
 * 90%. Turned off by hand, it stays off until someone turns it on.
 */
export function nextAltTextAutoSave(current: { on: boolean; manualOff: boolean }, rate: AltTextAutoSaveRate): boolean {
  if (current.manualOff) return false;
  if (rate.reviewed < ALT_TEXT_AUTO_SAVE_WINDOW) return false;
  const share = rate.asWritten / rate.reviewed;
  return current.on ? share >= ALT_TEXT_AUTO_SAVE_OFF_RATE : share >= ALT_TEXT_AUTO_SAVE_ON_RATE;
}

/** The automatic saving state the Media page shows. */
export type AltTextAutoSaveState = AltTextAutoSaveRate & {
  on: boolean;
  manual_off: boolean;
  /** Who turned it off by hand, when they did. */
  manual_off_by: string | null;
};

/** Shopify's limit on a file's alt text. */
export const SHOPIFY_ALT_TEXT_MAX_CHARS = 512;

/** One image's alt text as a person saves it, checked by the form and the Worker. */
export const altTextFieldsSchema = z.object({
  alt_text: z.string().trim().max(SHOPIFY_ALT_TEXT_MAX_CHARS, `Alt text is at most ${SHOPIFY_ALT_TEXT_MAX_CHARS} characters`),
});

/** Images per save: a page of the Media list. */
export const ALT_TEXT_SAVE_MAX = 250;

export const altTextSaveSchema = z.object({
  items: z
    .array(
      altTextFieldsSchema.extend({
        product_media_id: z.string().uuid(),
        expected_alt_text: z.string().max(SHOPIFY_ALT_TEXT_MAX_CHARS * 4).nullable(),
      })
    )
    .min(1)
    .max(ALT_TEXT_SAVE_MAX),
});

/** The Media page's and product page's alt text write: one row per image. */
export type AltTextSaveItem = {
  product_media_id: string;
  alt_text: string;
  /**
   * The alt text the person saw when they opened it. When it has changed since (someone else
   * saved), the save is refused rather than overwriting theirs. Automatic saving sends none.
   */
  expected_alt_text?: string | null;
};

/** An image's save refused because its alt text changed since it was opened. */
export const ALT_TEXT_STALE_ERROR = 'Someone changed this alt text since you opened it. Reopen it to see theirs.';

export type AltTextSaveResult = {
  product_media_id: string;
  ok: boolean;
  error?: string;
  /** Refused as stale (`ALT_TEXT_STALE_ERROR`): the alt text it has now. */
  current_alt_text?: string | null;
};

/** The Media page's alt text panel: automatic saving, how many images Suggest alt text would write for, and the last run. */
export type AltTextOverview = {
  auto_save: AltTextAutoSaveState;
  /** Images with no suggestion yet, or whose inputs changed. */
  due: number;
  /** Shopify writes are on here (off on dev: saves change jflo only). */
  writes_on: boolean;
  run: JobRunSummary | null;
};
